/**
 * @fileoverview Per-block paragraph spacing (space before / after)
 *
 * Purpose: Mirror Word-style “space before” / “space after” on `<p>` and
 * headings so tight line-height is not undermined by default paragraph gaps
 * (`margin-bottom` from `.prose-letter`). Values are stored as inline
 * `margin-top` / `margin-bottom` so they round-trip through HTML storage and
 * survive the Word altChunk export path (with `mso-margin-*-alt` mirroring
 * in `exportHtmlDocument.ts`).
 *
 * Every command runs `updateAttributes` once per configured type, which walks
 * the whole selection — so a drag-selection covering several paragraphs (or a
 * mix of paragraphs and headings, including ones nested in list items and
 * table cells) is updated in one go, exactly like the sibling `LineHeight`
 * extension. Resolving a single "active" block type instead would silently no-op
 * on any multi-block selection, because `editor.isActive()` only reports true
 * when one type covers the entire selection.
 */

import { Extension } from "@tiptap/core";

/** Space added by “Add space before/after paragraph” (matches ~1 line at 11pt). */
export const PARAGRAPH_SPACE_ADD_PT = "12pt";

/** Block types that carry per-paragraph spacing attributes. */
export const PARAGRAPH_SPACING_TYPES = ["paragraph", "heading"] as const;

/**
 * Explicit “no margin” on an edge — `0pt` avoids falsy attribute bugs and
 * overrides Word/browser “Auto” spacing when combined with MSO mirror CSS
 * on export.
 */
export const PARAGRAPH_MARGIN_NONE_PT = "0pt";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    paragraphSpacing: {
      addSpaceBeforeParagraph: () => ReturnType;
      removeSpaceBeforeParagraph: () => ReturnType;
      addSpaceAfterParagraph: () => ReturnType;
      removeSpaceAfterParagraph: () => ReturnType;
      resetParagraphSpacing: () => ReturnType;
    };
  }
}

export interface ParagraphSpacingOptions {
  types: string[];
  /** Org default `margin-top` applied when no per-paragraph override exists. */
  defaultMarginTop: string;
  /** Org default `margin-bottom` applied when no per-paragraph override exists. */
  defaultMarginBottom: string;
}

function marginAttrToStyle(
  prop: "margin-top" | "margin-bottom",
  value: unknown,
): Record<string, string> | Record<string, never> {
  if (value === null || value === undefined) return {};
  const s = String(value).trim();
  if (s === "") return {};
  return { style: `${prop}: ${s}` };
}

export const ParagraphSpacing = Extension.create<ParagraphSpacingOptions>({
  name: "paragraphSpacing",

  addOptions() {
    return {
      types: [...PARAGRAPH_SPACING_TYPES],
      defaultMarginTop: "",
      defaultMarginBottom: "",
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          paragraphMarginTop: {
            default: this.options.defaultMarginTop,
            parseHTML: (element) =>
              element.style.marginTop || this.options.defaultMarginTop,
            renderHTML: (attributes) =>
              marginAttrToStyle("margin-top", attributes.paragraphMarginTop),
          },
          paragraphMarginBottom: {
            default: this.options.defaultMarginBottom,
            parseHTML: (element) =>
              element.style.marginBottom || this.options.defaultMarginBottom,
            renderHTML: (attributes) =>
              marginAttrToStyle(
                "margin-bottom",
                attributes.paragraphMarginBottom,
              ),
          },
        },
      },
    ];
  },

  addCommands() {
    const types = this.options.types;

    return {
      addSpaceBeforeParagraph:
        () =>
        ({ commands }) =>
          types.every((type) =>
            commands.updateAttributes(type, {
              paragraphMarginTop: PARAGRAPH_SPACE_ADD_PT,
            }),
          ),
      removeSpaceBeforeParagraph:
        () =>
        ({ commands }) =>
          types.every((type) =>
            commands.updateAttributes(type, {
              paragraphMarginTop: PARAGRAPH_MARGIN_NONE_PT,
            }),
          ),
      addSpaceAfterParagraph:
        () =>
        ({ commands }) =>
          types.every((type) =>
            commands.updateAttributes(type, {
              paragraphMarginBottom: PARAGRAPH_SPACE_ADD_PT,
            }),
          ),
      removeSpaceAfterParagraph:
        () =>
        ({ commands }) =>
          types.every((type) =>
            commands.updateAttributes(type, {
              paragraphMarginBottom: PARAGRAPH_MARGIN_NONE_PT,
            }),
          ),
      resetParagraphSpacing:
        () =>
        ({ commands }) =>
          types.every((type) =>
            commands.resetAttributes(type, [
              "paragraphMarginTop",
              "paragraphMarginBottom",
            ]),
          ),
    };
  },
});
